"use client";

import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/app/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import Icon from "../../Icon";
import { ContactFormData } from "../../../lib/forms/contact-schema";

type InquiryDetailsSectionProps = {
  control: Control<ContactFormData>;
};

export function InquiryDetailsSection({ control }: InquiryDetailsSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="text-base md:text-lg font-normal text-ui-card mb-4 flex items-center gap-2">
        <Icon name="info-circle" size="md" className="text-ui-card/80" />
        {t("Inquiry Details")}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Subject */}
        <FormField
          control={control}
          name="subject"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-normal text-ui-card/90">
                {t("Subject")} <span className="text-red-500" aria-label={t("Required")}>*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="rounded-none border-ui-border bg-ui-page text-ui-card focus:ring-brand-accent focus:border-transparent h-auto px-4 py-3 text-sm font-light">
                    <SelectValue placeholder={t("Select a subject")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-ui-page border-ui-border rounded-none">
                  <SelectItem value="general_inquiry" className="text-ui-card focus:bg-ui-border">
                    {t("General Inquiry")}
                  </SelectItem>
                  <SelectItem value="booking_request" className="text-ui-card focus:bg-ui-border">
                    {t("Booking Request")}
                  </SelectItem>
                  <SelectItem value="complaint" className="text-ui-card focus:bg-ui-border">
                    {t("Complaint")}
                  </SelectItem>
                  <SelectItem value="feedback" className="text-ui-card focus:bg-ui-border">
                    {t("Feedback")}
                  </SelectItem>
                  <SelectItem value="other" className="text-ui-card focus:bg-ui-border">
                    {t("Other")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="text-xs text-red-600" />
            </FormItem>
          )}
        />

        {/* Preferred Contact Method */}
        <FormField
          control={control}
          name="preferredContactMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-normal text-ui-card/90">
                {t("Preferred Contact Method")}
              </FormLabel>
              <FormControl>
                <div className="space-y-2 pt-1">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      {...field}
                      value="email"
                      checked={field.value === "email"}
                      className="mr-3 w-4 h-4 text-brand-accent border-ui-border focus:ring-brand-accent cursor-pointer"
                    />
                    <span className="text-sm font-light text-ui-card group-hover:text-ui-card/80 transition-colors">
                      {t("Email")}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      {...field}
                      value="phone"
                      checked={field.value === "phone"}
                      className="mr-3 w-4 h-4 text-brand-accent border-ui-border focus:ring-brand-accent cursor-pointer"
                    />
                    <span className="text-sm font-light text-ui-card group-hover:text-ui-card/80 transition-colors">
                      {t("Phone")}
                    </span>
                  </label>
                </div>
              </FormControl>
              <FormMessage className="text-xs text-red-600" />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

